import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Copy, TriangleAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { useRequireScope } from '@/Hooks/useRequireScope';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { FLEET_ROUTES, type IssuedBridgeToken } from '../fleetApi';
import { apiMessage, type BranchOption } from '../fleetHelpers';

/**
 * Enrol a print bridge — the agent inside the venue that owns the printers.
 *
 * A bridge holds a real secret, unlike a device: a device fingerprint is
 * client-generated and travels in plaintext, so it authorises nothing, while a
 * bridge is issued a 256-bit random token stored only as an HMAC. That
 * difference is why this is a separate flow from device pairing and not a reuse
 * of it, and why the token is shown exactly once.
 */
export function EnrolBridgeDialog({
  open,
  branches,
  defaultBranchId,
  onClose,
  onEnrolled,
}: {
  open: boolean;
  branches: BranchOption[];
  defaultBranchId?: string;
  onClose: () => void;
  onEnrolled: (issued: IssuedBridgeToken) => void;
}) {
  const { t } = useTranslation();
  const requireScope = useRequireScope();
  const [branchId, setBranchId] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setName('');
    setBranchId(defaultBranchId ?? branches[0]?.id ?? '');
  }, [open, defaultBranchId, branches]);

  const outOfScope = Boolean(branchId) && !requireScope('BRANCH', branchId);

  const submit = async () => {
    if (!name.trim() || !branchId) {
      setError(t('fleet.bridges.nameAndBranchRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post<IssuedBridgeToken>(FLEET_ROUTES.bridges, {
        branchId,
        name: name.trim(),
      });
      onEnrolled(data);
      onClose();
    } catch (requestError) {
      setError(apiMessage(requestError, t('fleet.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('fleet.bridges.enrolTitle')}
      description={t('fleet.bridges.enrolHint')}
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={busy || outOfScope} onClick={() => void submit()}>
            {t('fleet.bridges.enrol')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field
          label={t('fleet.common.branch')}
          required
          hint={t('fleet.bridges.branchHint')}
          error={outOfScope ? t('fleet.printers.branchOutOfScope') : undefined}
        >
          <Select value={branchId} onChange={(event) => setBranchId(event.target.value)}>
            <option value="">{t('fleet.printers.chooseBranch')}</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('fleet.common.name')} required hint={t('fleet.bridges.nameHint')}>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={120}
            placeholder={t('fleet.bridges.namePlaceholder')}
          />
        </Field>
        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/**
 * The token, shown once.
 *
 * Nothing stores it in plaintext and no later read can recover it — the server
 * keeps only an HMAC keyed with the application secret, the same construction
 * as a pairing code. The dialog says that rather than leaving an operator to
 * discover it when they come back for the value tomorrow, and rotation is
 * named as the recovery path so nobody concludes the bridge has to be
 * re-enrolled.
 */
export function BridgeTokenDialog({
  issued,
  onClose,
}: {
  issued: IssuedBridgeToken | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const copy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.token);
      toast.success(t('fleet.bridges.tokenCopied'));
    } catch {
      toast.error(t('fleet.bridges.tokenCopyFailed'));
    }
  };

  return (
    <Dialog
      open={issued !== null}
      onClose={onClose}
      title={t('fleet.bridges.tokenTitle')}
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      }
    >
      {issued && (
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
            <Icon as={TriangleAlert} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <p>{t('fleet.bridges.tokenOnce')}</p>
          </div>
          <div className="rounded-lg border bg-subtle p-3">
            <code className="block break-all font-mono text-sm">{issued.token}</code>
          </div>
          <Button variant="outline" onClick={() => void copy()}>
            <Icon as={Copy} />
            {t('fleet.bridges.tokenCopy')}
          </Button>
          <p className="text-sm text-ink-muted">{t('fleet.bridges.tokenNextSteps')}</p>
        </div>
      )}
    </Dialog>
  );
}
